import { COLORS, Text } from "@asayinc/component-library";
import { Box, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

import { Card } from "src/components/atoms/Card";
import { RecentActivity, useRecentActivityQuery } from "src/store/dashboard";

dayjs.extend(relativeTime);

// Activity type icons and colors
const activityConfig: Record<
  string,
  { icon: string; color: string; label: string }
> = {
  tabulation_report_delivered: { icon: "📋", color: "#F57C00", label: "Proxy" },
  location_analysis_delivered: {
    icon: "🌍",
    color: "#00796B",
    label: "Location Analysis",
  },
  share_range_analysis_delivered: {
    icon: "📈",
    color: "#388E3C",
    label: "Share Range Analysis",
  },
  nobo_delivered: { icon: "👥", color: "#7B1FA2", label: "NOBO" },
};

function ActivityItem({ activity }: { activity: RecentActivity }) {
  const navigate = useNavigate();
  const config = activityConfig[activity.activityType] || {
    icon: "📄",
    color: "#1976D2",
    label: "Activity",
  };

  const getActivityRoute = (activityType: string, entityId: string) => {
    switch (activityType) {
      case "tabulation_report_delivered":
        return `/proxy-events/${entityId}/tabulation-reports`;
      case "share_range_analysis_delivered":
        return `/share-range-analysis-events/${entityId}/reports`;
      case "location_analysis_delivered":
        return `/location-analysis-events/${entityId}/reports`;
      case "nobo_delivered":
        return `/nobo-events/${entityId}/details`;
      default:
        return null;
    }
  };

  const handleClick = () => {
    const route = getActivityRoute(activity.activityType, activity.entityId);
    if (route) {
      navigate(route);
    }
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        py: 3,
        px: 2,
        borderBottom: `1px solid ${COLORS.P_OUTLINE}`,
        "&:last-child": { borderBottom: "none" },
        cursor: "pointer",
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: `${config.color}08`,
        },
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            backgroundColor: `${config.color}15`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Text variant="body3">{config.icon}</Text>
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
            <Text variant="subtitle2">{activity.title}</Text>
            <Box
              sx={{
                px: 1.5,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: `${config.color}15`,
              }}
            >
              <Text
                variant="caption"
                sx={{ color: config.color, fontWeight: 600 }}
              >
                {config.label}
              </Text>
            </Box>
          </Stack>
          <Text variant="body3" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
            {activity.description}
          </Text>
          <Stack direction="row" alignItems="center" spacing={2} mt={1}>
            <Text variant="caption" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
              {activity.issuerName}
            </Text>
            {activity.jobNumber && (
              <>
                <Text
                  variant="caption"
                  sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}
                >
                  •
                </Text>
                <Text
                  variant="caption"
                  sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}
                >
                  {activity.jobNumber}
                </Text>
              </>
            )}
            <Text variant="caption" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
              •
            </Text>
            <Text variant="caption" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
              {dayjs(activity.occurredAt).fromNow()}
            </Text>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

export function RecentActivitySection() {
  const { data: recentActivity } = useRecentActivityQuery();

  return (
    <Card title="Recent Activity">
      <Box
        sx={{
          maxHeight: 400,
          overflowY: "auto",
          border: `1px solid ${COLORS.P_OUTLINE}`,
          borderRadius: 1,
        }}
      >
        {recentActivity && recentActivity.length > 0 ? (
          recentActivity.map((activity) => (
            <ActivityItem key={activity.entityId} activity={activity} />
          ))
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Text variant="body3" sx={{ color: COLORS.P_MED_LOW_EMPHASIS }}>
              No recent activity
            </Text>
          </Box>
        )}
      </Box>
    </Card>
  );
}
